import React from "react";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, TrendingUp, ExternalLink, Sparkles } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

interface Startup {
  id: string;
  title: string;
  slug: string;
  short_description?: string;
  full_description?: string;
  category?: string;
  tags: string[];
  source?: string;
  source_url?: string;
  trend_score: number;
  trend_curve: Array<{ date: string; value: number }>;
  created_at: string;
}

interface BuildPhase {
  phase: string;
  steps: string[];
}

const StartupTrendDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [startup, setStartup] = useState<Startup | null>(null);
  const [buildSteps, setBuildSteps] = useState<BuildPhase[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingSteps, setIsGeneratingSteps] = useState(false);

  useEffect(() => {
    fetchStartup();
  }, [id]);

  const fetchStartup = async () => {
    if (!id) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('startups')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      
      // Type cast the data to match our interface
      const typedData: Startup = {
        ...data,
        trend_curve: Array.isArray(data.trend_curve) ? data.trend_curve as Array<{ date: string; value: number }> : []
      };
      
      setStartup(typedData);
      
      // Generate build steps automatically
      if (typedData) {
        generateBuildSteps(typedData);
      }
    } catch (error) {
      console.error('Error fetching startup:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const generateBuildSteps = async (startupData: Startup) => {
    setIsGeneratingSteps(true);
    try {
      const prompt = `Based on this startup idea, create a detailed 4-phase build plan:

Title: ${startupData.title}
Description: ${startupData.full_description || startupData.short_description}
Category: ${startupData.category}

Generate a structured build plan with these 4 phases:
1. Phase 1: Validation - Steps to validate the idea and market
2. Phase 2: MVP - Steps to build minimum viable product
3. Phase 3: Launch - Steps to launch and get initial users
4. Phase 4: Growth - Steps to scale and grow the business

For each phase, provide 4-6 specific, actionable steps.

Respond in valid JSON format:
{
  "phases": [
    {
      "phase": "Phase 1: Validation",
      "steps": ["Step 1", "Step 2", ...]
    },
    ...
  ]
}`;

      const { data: functionData, error: functionError } = await supabase.functions.invoke('enrich-startup', {
        body: { 
          ideaId: id,
          prompt,
          type: 'build_steps'
        }
      });

      if (functionError) throw functionError;

      if (functionData?.phases) {
        setBuildSteps(functionData.phases);
      }
    } catch (error) {
      console.error('Error generating build steps:', error);
      // Provide fallback generic steps
      setBuildSteps([
        {
          phase: "Phase 1: Validation",
          steps: [
            "Research the market and competitors",
            "Interview potential customers",
            "Create a landing page to gauge interest",
            "Build a waitlist",
            "Validate pricing assumptions"
          ]
        },
        {
          phase: "Phase 2: MVP",
          steps: [
            "Define core features (top 3)",
            "Choose technology stack",
            "Build basic prototype",
            "Test with early users",
            "Iterate based on feedback"
          ]
        },
        {
          phase: "Phase 3: Launch",
          steps: [
            "Prepare launch materials",
            "Launch on Product Hunt",
            "Share on relevant communities",
            "Reach out to early adopters",
            "Collect user feedback"
          ]
        },
        {
          phase: "Phase 4: Growth",
          steps: [
            "Analyze user metrics",
            "Implement growth loops",
            "Scale marketing channels",
            "Build partnerships",
            "Optimize conversion funnel"
          ]
        }
      ]);
    } finally {
      setIsGeneratingSteps(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/20 p-6">
        <div className="max-w-6xl mx-auto space-y-6">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-12 w-3/4" />
          <Skeleton className="h-96 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (!startup) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/20 p-6">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-2xl font-bold mb-4">Startup not found</h1>
          <Button onClick={() => navigate('/startup-explorer')}>
            Back to Explorer
          </Button>
        </div>
      </div>
    );
  }

  const trendIndicator = startup.trend_score >= 75 
    ? { icon: "🔥", label: "Hot", color: "text-red-500" }
    : startup.trend_score >= 60 
    ? { icon: "📈", label: "Rising", color: "text-orange-500" }
    : startup.trend_score >= 40
    ? { icon: "💡", label: "Emerging", color: "text-yellow-500" }
    : { icon: "🌱", label: "New", color: "text-green-500" };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20 p-6">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/startup-explorer')}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Explorer
          </Button>
        </div>

        {/* Title Section */}
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h1 className="text-4xl font-bold mb-3">{startup.title}</h1>
              {startup.short_description && (
                <p className="text-xl text-muted-foreground">
                  {startup.short_description}
                </p>
              )}
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className={`flex items-center gap-2 text-lg font-semibold ${trendIndicator.color}`}>
                <span className="text-2xl">{trendIndicator.icon}</span>
                <span>{trendIndicator.label}</span>
              </div>
              <Badge variant="outline" className="text-lg px-3 py-1">
                Score: {startup.trend_score}
              </Badge>
            </div>
          </div>

          {/* Tags and Source */}
          <div className="flex flex-wrap gap-2 items-center">
            {startup.category && (
              <Badge variant="default" className="text-sm">
                {startup.category}
              </Badge>
            )}
            {startup.tags?.map((tag, idx) => (
              <Badge key={idx} variant="secondary" className="text-sm">
                {tag}
              </Badge>
            ))}
            {startup.source && (
              <Badge variant="outline" className="text-sm capitalize">
                Source: {startup.source}
              </Badge>
            )}
            {startup.source_url && (
              <Button
                variant="link"
                size="sm"
                onClick={() => window.open(startup.source_url, '_blank')}
                className="gap-1"
              >
                View Source
                <ExternalLink className="w-3 h-3" />
              </Button>
            )}
          </div>
        </div>

        {/* Trend Chart */}
        {startup.trend_curve && startup.trend_curve.length > 0 && (
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-primary" />
              <h2 className="text-xl font-semibold">Interest Over Time</h2>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={startup.trend_curve}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis 
                  dataKey="date" 
                  className="text-xs"
                  tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis className="text-xs" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                  labelFormatter={(value) => new Date(value).toLocaleDateString()}
                />
                <Line 
                  type="monotone" 
                  dataKey="value" 
                  stroke="hsl(var(--primary))" 
                  strokeWidth={3}
                  dot={{ fill: 'hsl(var(--primary))', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* Full Description */}
        {startup.full_description && (
          <Card className="p-6">
            <h2 className="text-2xl font-semibold mb-4">About This Startup</h2>
            <div className="prose prose-slate dark:prose-invert max-w-none">
              {startup.full_description.split('\n\n').map((paragraph, idx) => (
                <p key={idx} className="mb-4 text-muted-foreground leading-relaxed">
                  {paragraph}
                </p>
              ))}
            </div>
          </Card>
        )}

        {/* Build Steps */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-6">
            <Sparkles className="w-5 h-5 text-primary" />
            <h2 className="text-2xl font-semibold">How to Build This Startup</h2>
          </div>
          
          {isGeneratingSteps ? (
            <div className="space-y-4">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : buildSteps.length > 0 ? (
            <Accordion type="single" collapsible className="w-full">
              {buildSteps.map((phase, idx) => (
                <AccordionItem key={idx} value={`phase-${idx}`}>
                  <AccordionTrigger className="text-lg font-semibold">
                    {phase.phase}
                  </AccordionTrigger>
                  <AccordionContent>
                    <ol className="list-decimal list-inside space-y-2 ml-2">
                      {phase.steps.map((step, stepIdx) => (
                        <li key={stepIdx} className="text-muted-foreground">
                          {step}
                        </li>
                      ))}
                    </ol>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          ) : (
            <p className="text-muted-foreground">Loading build steps...</p>
          )}
        </Card>
      </div>
    </div>
  );
};

export default StartupTrendDetail;
